import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import AddressInput from "../components/AddressInput";
import PhoneInput from "react-phone-number-input";
import PhoneVerification from "../components/PhoneVerification";
import { getMe, updateUserProfile, UserProfileDto } from "../api/services";
import { useAuth } from "../contexts/AuthContext";

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  // `ProtectedRoute` bounces here with `state.from` set when required
  // profile fields are missing. We use the same signal to show the
  // "please complete your info" banner.
  const requiredInfoMissing = Boolean(
    (location.state as { from?: unknown } | null)?.from,
  );

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [phone, setPhone] = useState("");
  // Verification state for the number currently in the field. Seeded on load
  // from the API, cleared whenever the tenant edits the number.
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");

  // We lock first/last name to read-only once the server has a value for
  // them — those are signing-relevant fields the user shouldn't be able
  // to change freely. But if either is empty on load (legacy account, or
  // a never-completed sign-up), let the user fill it in here.
  const [firstNameEditable, setFirstNameEditable] = useState(false);
  const [lastNameEditable, setLastNameEditable] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getMe()
      .then((res) => {
        const data: UserProfileDto = res.data;
        setEmail(data.email);
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setFirstNameEditable(!data.firstName?.trim());
        setLastNameEditable(!data.lastName?.trim());
        setMiddleName(data.middleName ?? "");
        setPhone(data.phone ?? "");
        // PhoneVerification re-checks against the API on mount; this just
        // avoids a flash of the "verify this number" prompt.
        setPhoneVerified(false);
        setDob(data.dob ? data.dob.slice(0, 10) : "");
        setAddress(data.location?.address ?? "");
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        setError(t("profile.errorLoading"));
      })
      .finally(() => setLoading(false));
  }, [t]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    updateUserProfile({
      firstName,
      lastName,
      middleName,
      phone,
      email,
      dob: dob || undefined,
      address: address || undefined,
    })
      .then(() => {
        refreshUser();
        navigate("/dashboard");
      })
      .catch((err) => {
        console.error(err);
        setError(t("profile.errorSave"));
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="mt-6">
        <GoBack to="/dashboard" />
        <p>{t("profile.loading")}</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <GoBack to="/dashboard" />
      <h2 className="mb-6">{t("profile.title")}</h2>
      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          {requiredInfoMissing && (
            <div className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
              {t("profile.requiredInfoMissing")}
            </div>
          )}
          {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{error}</div>}
          <form onSubmit={handleSubmit} className="max-w-[500px]">
            <div className="mb-4">
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.email")}
              </label>
              <input
                type="email"
                id="email"
                className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-600"
                value={email}
                readOnly
              />
            </div>

            <div className="mb-4">
              <label htmlFor="firstName" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.firstName")}
                {firstNameEditable && <span className="text-danger"> *</span>}
              </label>
              <input
                type="text"
                id="firstName"
                className={
                  firstNameEditable
                    ? "w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    : "w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-600"
                }
                value={firstName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFirstName(e.target.value)
                }
                readOnly={!firstNameEditable}
                required={firstNameEditable}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="lastName" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.lastName")}
                {lastNameEditable && <span className="text-danger"> *</span>}
              </label>
              <input
                type="text"
                id="lastName"
                className={
                  lastNameEditable
                    ? "w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    : "w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-600"
                }
                value={lastName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setLastName(e.target.value)
                }
                readOnly={!lastNameEditable}
                required={lastNameEditable}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="middleName" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.middleName")}
              </label>
              <input
                type="text"
                id="middleName"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                value={middleName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setMiddleName(e.target.value)
                }
              />
            </div>

            <div className="mb-4">
              <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.phone")}{" "}
                <span className="text-danger">*</span>
              </label>
              <PhoneInput
                id="phone"
                international={false}
                defaultCountry="US"
                value={phone}
                onChange={(val) => {
                  setPhone(val || "");
                  setPhoneVerified(false);
                }}
                placeholder="(123) 456-7890"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                required
              />
              <PhoneVerification
                phone={phone}
                verified={phoneVerified}
                onVerifiedChange={setPhoneVerified}
                onPhoneNormalized={setPhone}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="dob" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.dob")} <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                id="dob"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                value={dob}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setDob(e.target.value)
                }
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="address" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("profile.labels.address")}{" "}
                <span className="text-danger">*</span>
              </label>
              <AddressInput
                id="address"
                value={address}
                onChange={setAddress}
                placeholder={t("profile.placeholders.address")}
                required
              />
            </div>

            <button
              type="submit"
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:bg-gray-300"
              disabled={saving || (!!phone && !phoneVerified)}
            >
              {saving ? t("profile.buttons.saving") : t("profile.buttons.save")}
            </button>
            {!!phone && !phoneVerified && (
              <p className="mt-2 text-xs text-gray-500">
                Verify your phone number to save your profile.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
